import { useState, useEffect } from 'react';
import Modal from '../shared/Modal';
import { getTeams } from '../../utils/api';
import { ADMIN_EMAILS } from '../../config';

export default function EditTeamLeadModal({ open, onClose, lead, onSave, showToast }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('Team Lead');
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [status, setStatus] = useState('active');
  const [saving, setSaving] = useState(false);
  const [availableTeams, setAvailableTeams] = useState([]);

  // Load actual teams from the spreadsheet whenever the modal opens
  useEffect(() => {
    if (!open) return;
    getTeams()
      .then(data => setAvailableTeams((data || []).map(t => t.name).sort()))
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    if (lead) {
      setName(lead.name || '');
      setEmail(lead.email || '');
      setRole(lead.role || 'Team Lead');
      setStatus(lead.status || 'active');
      const teamsStr = (lead.teams || '').replace('All Teams', '');
      // Use pipe as delimiter; fall back to comma for legacy data
      const delimiter = teamsStr.includes('|') ? '|' : ',';
      setSelectedTeams(teamsStr.split(delimiter).map(t => t.trim()).filter(Boolean));
    }
  }, [lead]);

  function toggleTeam(team) {
    setSelectedTeams(prev =>
      prev.includes(team) ? prev.filter(t => t !== team) : [...prev, team]
    );
  }

  async function handleSave() {
    if (!name.trim() || !email.trim()) {
      showToast('Name and email are required.');
      return;
    }
    setSaving(true);
    const updated = {
      ...lead,
      email: email.trim(),
      name: name.trim(),
      role,
      teams: selectedTeams.join(' | '),
      status,
    };
    try {
      await onSave(updated);
      onClose();
    } finally {
      setSaving(false);
    }
  }

  if (!lead) return null;

  const isHardAdmin = ADMIN_EMAILS.includes(lead.email);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit Team Lead"
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-dark" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </>
      }
    >
      <div className="form-group">
        <label className="form-label">Name</label>
        <input className="form-input" value={name} onChange={e => setName(e.target.value)} placeholder="Full name" />
      </div>
      <div className="form-group">
        <label className="form-label">Google Account Email</label>
        <input
          className="form-input"
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="Their Google email for login"
          disabled={isHardAdmin}
          style={isHardAdmin ? { opacity: 0.6 } : {}}
        />
      </div>
      {!isHardAdmin && (
        <div className="form-group">
          <label className="form-label">Role</label>
          <select className="form-input" value={role} onChange={e => setRole(e.target.value)}>
            <option>Team Lead</option>
            <option>Admin</option>
          </select>
        </div>
      )}
      {!isHardAdmin && (
        <div className="form-group">
          <label className="form-label">Status</label>
          <select className="form-input" value={status} onChange={e => setStatus(e.target.value)}>
            <option value="active">Active</option>
            <option value="not-invited">Not Invited</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      )}
      <div className="form-group">
        <label className="form-label">Teams{isHardAdmin ? ' (for Team Lead View)' : ''}</label>
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 12px', maxHeight: 220, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {availableTeams.length === 0 ? (
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--muted)', fontWeight: 300 }}>Loading teams…</div>
          ) : availableTeams.map(t => (
            <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700, color: 'var(--black)' }}>
              <input
                type="checkbox"
                checked={selectedTeams.includes(t)}
                onChange={() => toggleTeam(t)}
                style={{ accentColor: 'var(--black)' }}
              />
              {t}
            </label>
          ))}
        </div>
        {isHardAdmin && (
          <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', marginTop: 6 }}>
            Select which teams appear in your Team Lead View when you switch to TL mode.
          </div>
        )}
      </div>
    </Modal>
  );
}
